import { Link } from "@tanstack/react-router";
import { Home, MessageCircle, Settings } from "lucide-react";

import { useGetCounts } from "@/api/inbox";
import { useLanguage } from "@/contexts/language";
import { Num } from "@/features/chat/lib";
import { hapticSelection } from "@/lib/haptics";
import { cn } from "@/lib/utils";

// Desktop counterpart of BottomNav: a vertical rail pinned to the inline start
// (right in Arabic, left in English), branded at the top. Swapped purely by the
// md: breakpoint — BottomNav hides at the same width, so only one is ever
// visible and there is no layout flash on first paint.
//
// `collapsed` shrinks it to a WhatsApp-style icon rail (logo kept on top) so
// the conversations aside can sit beside it on chat screens.

function NavItem({
  to,
  label,
  icon,
  badge,
  exact,
  collapsed,
}: {
  to: "/home" | "/chats" | "/settings";
  label: string;
  icon: React.ReactNode;
  badge?: number;
  exact?: boolean;
  collapsed?: boolean;
}) {
  return (
    <Link
      to={to}
      // "/" would match every route under fuzzy matching, so Home opts out.
      activeOptions={exact ? { exact: true } : undefined}
      onClick={hapticSelection}
      title={collapsed ? label : undefined}
      aria-label={label}
      className={cn(
        "tw:relative tw:flex tw:items-center tw:rounded-xl tw:text-sm tw:font-medium tw:text-muted-foreground tw:no-underline tw:transition-colors tw:hover:bg-muted",
        collapsed
          ? "tw:justify-center tw:px-0 tw:py-2.5"
          : "tw:gap-3 tw:px-3 tw:py-2.5",
      )}
      activeProps={{ className: "tw:bg-brand-50 tw:!text-brand" }}
    >
      <span className="tw:flex tw:shrink-0 tw:items-center">{icon}</span>
      {!collapsed && (
        <span className="tw:min-w-0 tw:flex-1 tw:truncate">{label}</span>
      )}
      {badge != null && badge > 0 && (
        <span
          className={cn(
            "tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-brand tw:font-semibold tw:text-brand-foreground",
            collapsed
              ? "tw:absolute tw:-top-0.5 tw:end-0.5 tw:h-4 tw:min-w-4 tw:px-1 tw:text-[10px]"
              : "tw:h-5 tw:min-w-5 tw:px-1.5 tw:text-[11px]",
          )}
        >
          <Num>{badge > 99 ? "99+" : badge}</Num>
        </span>
      )}
    </Link>
  );
}

export function SideNav({ collapsed = false }: { collapsed?: boolean }) {
  const { t } = useLanguage();
  const { data: counts } = useGetCounts();

  return (
    <aside
      className={cn(
        "tw:fixed tw:inset-y-0 tw:start-0 tw:z-50 tw:hidden tw:flex-col tw:border-e tw:border-border tw:bg-background tw:md:flex",
        collapsed ? "tw:w-[4.5rem]" : "tw:w-60",
      )}
    >
      <Link
        to="/home"
        className={cn(
          "tw:flex tw:items-center tw:border-b tw:border-border tw:py-4 tw:no-underline",
          collapsed ? "tw:justify-center tw:px-2" : "tw:gap-2.5 tw:px-4",
        )}
      >
        <img src="/logo.svg" alt="" className="tw:size-9 tw:shrink-0" />
        {!collapsed && (
          <span className="tw:truncate tw:text-lg tw:font-semibold tw:text-foreground">
            {t("Mandoob:AppName")}
          </span>
        )}
      </Link>

      {/* Padding identical in both states so items keep the exact same
          vertical position when the rail collapses/expands. */}
      <nav className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:gap-1 tw:p-3">
        <NavItem
          to="/home"
          exact
          collapsed={collapsed}
          label={t("Mandoob:Nav.Home")}
          icon={<Home className="tw:size-5" strokeWidth={1.75} />}
        />
        <NavItem
          to="/chats"
          collapsed={collapsed}
          label={t("Mandoob:Nav.Chats")}
          icon={<MessageCircle className="tw:size-5" strokeWidth={1.75} />}
          badge={counts?.unread}
        />
        <NavItem
          to="/settings"
          collapsed={collapsed}
          label={t("Mandoob:Nav.Settings")}
          icon={<Settings className="tw:size-5" strokeWidth={1.75} />}
        />
      </nav>
    </aside>
  );
}
